import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Gavel, Check, X, FileQuestion, ShieldCheck, Loader2 } from "lucide-react";
import { api, type AuditEntryDTO } from "@/lib/api";
import { JourneyTimeline } from "@/components/JourneyTimeline";
import { StageNotReached } from "@/components/StageNotReached";
import { Badge, Button, Card, Spinner, Textarea } from "@/components/ui/primitives";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { formatDateTime } from "@/lib/utils";
import { markStageForPlayback, useAutoRun } from "@/lib/playback";
import { usePlayback } from "@/hooks/usePlayback";

export const Route = createFileRoute("/apps/$id/decision")({ component: DecisionPage });

function DecisionPage() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["decision", id], queryFn: () => api.getDecision(id) });

  const submit = useMutation({
    mutationFn: (args: { action: string; justification: string }) => api.submitDecision(id, args.action, args.justification),
    onSuccess: () => qc.invalidateQueries(),
  });
  const advance = useMutation({
    mutationFn: () => api.advance(id),
    onSuccess: async () => {
      markStageForPlayback(id, "decision");
      await qc.invalidateQueries();
    },
  });
  const [discretionOpen, setDiscretionOpen] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [justification, setJustification] = useState("");
  const [declineReason, setDeclineReason] = useState("");

  const playback = usePlayback({
    stage: "decision",
    appId: id,
    events: data?.journey_events ?? [],
    enabled: Boolean(data?.reached),
    cardCount: 0,
  });

  // Auto-run terminates at Decision: clear the flag once the viewer has
  // arrived and the playback (if any) has settled.
  const { isAutoRunning, stopAutoRun } = useAutoRun();
  useEffect(() => {
    if (isAutoRunning(id) && playback.status === "complete") {
      stopAutoRun();
    }
  }, [id, isAutoRunning, stopAutoRun, playback.status]);

  if (isLoading) return <div className="flex items-center gap-2 text-muted-foreground"><Spinner /> Loading decision…</div>;
  if (!data) return null;
  if (!data.reached) {
    return (
      <StageNotReached
        title="Decision hasn't been made yet"
        description="The Decision Agent applies the DECISION.AUTO routing rule once the application reaches this stage — auto-approving clean Tier A applications, auto-declining hard fails, and routing everything else to the officer."
        primaryLabel="Run decision routing"
        onPrimary={() => advance.mutate()}
        primaryDisabled={advance.isPending}
      />
    );
  }
  const app = data.detail;
  const finalized = app.decided_at != null || app.status.startsWith("auto_") || app.status === "declined" || app.status === "approved_with_discretion";

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
      <div className="space-y-6">
        <header>
          <h2 className="flex items-center gap-2 text-xl font-semibold">
            <Gavel className="h-5 w-5 text-brand" strokeWidth={1.9} /> Decision
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Apply an approval / decline / defer decision. Discretion overrides require a written justification — all
            actions are appended to the audit log.
          </p>
        </header>

        {!playback.revealOutcome ? (
          <Card className="flex items-center gap-3 p-5 text-sm text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin text-brand" strokeWidth={2.2} />
            <div>
              <div className="font-semibold text-foreground">Decision Agent is routing this application…</div>
              <div className="text-xs">Applying the DECISION.AUTO rule against the risk + policy assessment.</div>
            </div>
          </Card>
        ) : (
          <>
            <Card className="p-5 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[0.72rem] uppercase tracking-wider text-muted-foreground">Current status</span>
                <Badge variant="muted">{app.status_label}</Badge>
                {app.risk_tier && <Badge variant="default">Tier {app.risk_tier}</Badge>}
                {app.officer_discretion_used && (
                  <Badge variant="warn">
                    <ShieldCheck className="h-3 w-3" strokeWidth={2.2} /> Discretion used
                  </Badge>
                )}
              </div>
              {app.decision_reason && <p className="text-sm">{app.decision_reason}</p>}
              {app.officer_justification && (
                <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
                  <div className="mb-0.5 text-[0.72rem] uppercase tracking-wider text-muted-foreground">Officer justification</div>
                  {app.officer_justification}
                </div>
              )}
              {app.decided_at && <div className="text-xs text-muted-foreground">Decided {formatDateTime(app.decided_at)}</div>}
            </Card>

            {!finalized && (
              <Card className="p-5 space-y-4">
                <h3 className="text-base font-semibold">Officer actions</h3>
                <div className="flex flex-wrap gap-2">
                  <Button variant="primary" onClick={() => submit.mutate({ action: "approve", justification: "" })} disabled={submit.isPending}>
                    <Check className="h-4 w-4" strokeWidth={2.2} /> Approve
                  </Button>
                  <Dialog open={discretionOpen} onOpenChange={setDiscretionOpen}>
                    <DialogTrigger asChild>
                      <Button variant="brand">
                        <ShieldCheck className="h-4 w-4" strokeWidth={2.2} /> Approve with discretion
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Approve with officer discretion</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-2 text-sm">
                        <p className="text-muted-foreground">Record the rationale for exercising discretion. This will be attached to the audit log.</p>
                        <Textarea value={justification} onChange={(e) => setJustification(e.target.value)} placeholder="e.g. Borrower has 7+ years of stable employment and low historical utilization; DTI is inside the policy ceiling." />
                      </div>
                      <DialogFooter>
                        <Button
                          variant="brand"
                          disabled={!justification.trim() || submit.isPending}
                          onClick={() => {
                            submit.mutate({ action: "approve_with_discretion", justification });
                            setDiscretionOpen(false);
                            setJustification("");
                          }}
                        >
                          Confirm approval
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                  <Dialog open={declineOpen} onOpenChange={setDeclineOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline">
                        <X className="h-4 w-4" strokeWidth={2.2} /> Decline
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Decline application</DialogTitle>
                      </DialogHeader>
                      <Textarea value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} placeholder="Reason for decline (visible to applicant communications team)" />
                      <DialogFooter>
                        <Button
                          variant="destructive"
                          disabled={!declineReason.trim() || submit.isPending}
                          onClick={() => {
                            submit.mutate({ action: "decline", justification: declineReason });
                            setDeclineOpen(false);
                            setDeclineReason("");
                          }}
                        >
                          Confirm decline
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                  <Button variant="outline" onClick={() => submit.mutate({ action: "needs_info", justification: "" })}>
                    <FileQuestion className="h-4 w-4" strokeWidth={2.2} /> Request more info
                  </Button>
                </div>
              </Card>
            )}
          </>
        )}

        <section>
          <h3 className="mb-3 text-base font-semibold">Agent activity</h3>
          <JourneyTimeline events={data.journey_events} playback={playback} />
        </section>

        <section>
          <h3 className="mb-3 text-base font-semibold">Audit log</h3>
          <Card>
            <ul className="divide-y divide-border">
              {data.audit.length === 0 && <li className="p-4 text-sm text-muted-foreground">No officer actions yet.</li>}
              {data.audit.map((a) => (
                <AuditRow key={a.id} entry={a} />
              ))}
            </ul>
          </Card>
        </section>
      </div>

      <aside className="sticky top-8 space-y-4">
        <Card className="p-5">
          <div className="text-[0.72rem] uppercase tracking-wider text-muted-foreground">Recommended action</div>
          {!playback.revealOutcome ? (
            <>
              <div className="mt-2 flex items-center gap-2 text-lg font-semibold text-brand">
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.2} />
                Routing…
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                The Decision Agent is evaluating DECISION.AUTO against this application's risk tier, findings and policy results.
              </p>
            </>
          ) : (
            <>
              <div className="mt-2 text-lg font-semibold">{finalized ? "Finalized" : "Awaiting officer action"}</div>
              <p className="mt-2 text-sm text-muted-foreground">
                {finalized
                  ? "This application has been resolved and appears in the Completed tab."
                  : "Use the action panel to approve, decline, or request more information."}
              </p>
            </>
          )}
        </Card>
      </aside>
    </div>
  );
}

const ACTOR_LABELS: Record<string, string> = {
  "agent:reader": "Reader Agent",
  "agent:identity_check": "Identity Check Agent",
  "agent:address_verification": "Address Verification Agent",
  "agent:bureau": "Bureau Agent",
  "agent:income_verification": "Income Verification Agent",
  "agent:affordability": "Affordability Agent",
  "agent:fraud_signals": "Fraud Signals Agent",
  "agent:analyst": "Analyst Agent",
  "agent:compliance": "Compliance Agent",
  "agent:decision": "Decision Agent",
  system: "System",
};

const STAGE_TONES: Record<string, string> = {
  intake: "bg-brand/10 text-brand border-brand/30",
  analysis: "bg-warn/10 text-warn border-warn/30",
  coordination: "bg-accent/10 text-accent border-accent/30",
  decision: "bg-success/10 text-success border-success/30",
};

function formatActor(actor: string): string {
  return ACTOR_LABELS[actor] ?? actor;
}

function splitAction(action: string): { stage: string; event: string } {
  const idx = action.indexOf(":");
  if (idx === -1) return { stage: "", event: action };
  return { stage: action.slice(0, idx), event: action.slice(idx + 1).replace(/_/g, " ") };
}

function formatDetailValue(v: unknown): string {
  if (v == null) return "—";
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "number") return String(v);
  if (typeof v === "boolean") return v ? "yes" : "no";
  if (typeof v === "string") return v;
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

function humaniseKey(k: string): string {
  return k.replace(/_/g, " ").replace(/\bpct\b/gi, "%");
}

function AuditRow({ entry }: { entry: AuditEntryDTO }) {
  const { stage, event } = splitAction(entry.action);
  const details =
    entry.details && typeof entry.details === "object" && !Array.isArray(entry.details)
      ? (entry.details as Record<string, unknown>)
      : null;
  const tone = STAGE_TONES[stage] ?? "bg-muted/40 text-muted-foreground border-border";
  return (
    <li className="p-4 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        {stage && (
          <span className={`rounded-md border px-2 py-0.5 text-[0.7rem] font-semibold uppercase tracking-wider ${tone}`}>
            {stage}
          </span>
        )}
        <span className="font-medium text-foreground">{event}</span>
        <span className="text-xs text-muted-foreground">·</span>
        <span className="text-xs text-muted-foreground">{formatActor(entry.actor)}</span>
        <span className="ml-auto text-xs text-muted-foreground">{formatDateTime(entry.at)}</span>
      </div>
      {details && Object.keys(details).length > 0 && (
        <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
          {Object.entries(details).map(([k, v]) => (
            <div key={k} className="flex items-baseline gap-1">
              <dt className="text-muted-foreground/70">{humaniseKey(k)}:</dt>
              <dd className="font-medium text-foreground/80">{formatDetailValue(v)}</dd>
            </div>
          ))}
        </dl>
      )}
    </li>
  );
}
